import React, { useState, useEffect } from "react";
import { Layout, Table, Tag, Button, Space, Tabs, Row, Col, Input, Card, message } from "antd";
import { CheckCircleOutlined, EyeOutlined, SearchOutlined, ClearOutlined, FilterOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { isAnyPermissionAllowed } from "../../../utils/permissionHelper";

const { Content } = Layout;
const { TextArea } = Input;

const RequestApproval = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");

    // Filters
    const [searchFilter, setSearchFilter] = useState("");

    // Applied Filters
    const [appliedSearch, setAppliedSearch] = useState("");

    useEffect(() => {
        fetchRequests(1, pagination.pageSize, activeTab, appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, appliedSearch]);

    const fetchRequests = async (
        page = 1,
        limit = 10,
        tabKey = activeTab,
        searchVal = appliedSearch
    ) => {
        try {
            setLoading(true);
            const params = { tab: tabKey, page, limit };
            if (searchVal) params.search = searchVal;

            const response = await api.get("/inventory/material_request/list.php", { params });
            if (response.data.status === "success") {
                setRequests(response.data.dataset || []);
                setPagination({
                    current: page,
                    pageSize: limit,
                    total: response.data.total || 0,
                });
            } else {
                message.error("Failed to fetch material requests.");
            }
        } catch (error) {
            message.error("Failed to connect to material requests endpoint.");
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (pag) => {
        fetchRequests(pag.current, pag.pageSize, activeTab, appliedSearch);
    };

    const handleFilter = () => {
        setAppliedSearch(searchFilter);
    };

    const handleClear = () => {
        setSearchFilter("");
        setAppliedSearch("");
    };



    const tabItems = [
        { key: 'pending', label: 'Pending Approval' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' }
    ];

    const columns = [
        {
            title: "Reference",
            dataIndex: "mr_ref",
            key: "mr_ref",
            render: (text) => <strong style={{ color: 'var(--fluid-primary)' }}>{text}</strong>
        },
        {
            title: "Required Date",
            dataIndex: "required_date",
            key: "required_date",
            render: (date) => dayjs(date).format("YYYY-MM-DD")
        },
        {
            title: "Items Count",
            dataIndex: "total_items",
            key: "total_items",
            align: 'center',
        },
        {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },

        {
            title: "Requested By",
            dataIndex: "creator_name",
            key: "creator_name",
            render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "-"
        },
        {
            title: "Requested Date",
            dataIndex: "req_dt",
            key: "req_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "-"
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            render: (_, record) => {
                const canViewMR = isAnyPermissionAllowed(["material_request_list", "material_request_view_approvals"]);
                return (
                    <Button
                        type="primary"
                        className={activeTab === 'pending' ? "btn-review" : "btn-view"}
                        icon={activeTab === 'pending' ? <CheckCircleOutlined /> : <EyeOutlined />}
                        size="small"
                        onClick={() => {
                            if (activeTab === 'pending') {
                                navigate(`/inventory/material-request/approve/${record.mr_id}`);
                            } else {
                                if (canViewMR) {
                                    navigate(`/inventory/material-request/${record.mr_id}/view`);
                                } else {
                                    message.warning("You do not have permission to view Material Request details.");
                                }
                            }
                        }}
                    >
                        {activeTab === 'pending' ? 'Review' : 'View'}
                    </Button>
                );
            }
        }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Material Request Approvals' },
                ]} />
                <h1 className="fluid-page-title">
                    <CheckCircleOutlined style={{ marginRight: 12 }} />Request Approvals
                </h1>
                
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card">
                    <div style={{ marginBottom: 20 }}>
                        <Row gutter={[16, 16]} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search Reference:</div>
                                <Input
                                    placeholder="MR-XXXXXXXX"
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    onPressEnter={handleFilter}
                                />
                            </Col>

                            <Col xs={24} sm={24} md={5}>
                                <div className="fluid-filter-actions" style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <Button
                                        type="primary"
                                        icon={<FilterOutlined />}
                                        onClick={handleFilter}
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        style={{ marginBottom: 16 }}
                    />

                    <Table
                        columns={columns}
                        dataSource={requests}
                        rowKey="mr_id"
                        loading={loading}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={pagination}
                        onChange={handleTableChange}
                    />
                </Card>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default RequestApproval;
